import { useEffect, type RefObject } from "react";

/**
 * Scroll reveal for the marketing surface: `data-js` on `<html>` arms the CSS
 * that hides `.gs-reveal` (see `styles/site.css`), and this observer is what
 * un-hides it.
 *
 * The two ship together on purpose. Arming without an observer leaves the
 * first `.gs-reveal` element anyone writes permanently invisible, and the
 * attribute is what makes that failure mode reachable — so it is set only
 * once an observer exists to answer for it. No JS, or no
 * IntersectionObserver, and nothing is ever hidden. It is removed again on
 * unmount: the rule it arms is `.gs-site`-scoped, so its lifetime is the
 * marketing layout's, not the document's.
 *
 * `root` is the layout element. Child routes mount their content after this
 * effect runs and swap it on every navigation, so a MutationObserver picks up
 * the `.gs-reveal` nodes that appear later.
 */
export function useScrollReveal(root: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = root.current;
    if (!el || typeof IntersectionObserver === "undefined") return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.setAttribute("data-shown", "");
          io.unobserve(entry.target);
        }
      },
      // Fire a little before the element's top edge reaches the fold, so the
      // animation reads as arrival rather than as a late correction.
      { rootMargin: "0px 0px -8% 0px" },
    );
    const observeAll = () => {
      for (const node of el.querySelectorAll(".gs-reveal:not([data-shown])")) {
        io.observe(node);
      }
    };
    observeAll();

    const mo = new MutationObserver(observeAll);
    mo.observe(el, { childList: true, subtree: true });

    document.documentElement.setAttribute("data-js", "");
    return () => {
      mo.disconnect();
      io.disconnect();
      document.documentElement.removeAttribute("data-js");
    };
  }, [root]);
}
